/**
 * Módulo CANÓNICO de moneda — expansión Brasil (auditoría 2026-07-12).
 *
 * COPIA dual-repo del original en goautos-admin/src/lib/currency.ts (regla del
 * proyecto: los cambios se duplican a mano en ambos repos y este archivo debe
 * mantenerse idéntico al de admin, donde 190 aserciones del harness
 * `test:currency` fijan que el output CLP es byte-idéntico al legacy).
 *
 * PURO: sin React ni Supabase — usable en server (generateMetadata, og route,
 * send-email*) y client.
 */

export const DEFAULT_CURRENCY = 'CLP';

/**
 * Mismo valor que DEFAULT_IVA_PERCENTAGE (utils/sellerCalculation). La exención se
 * decide por régimen (consignación > vehicles.iva_exento > clients.ventas_exentas_iva),
 * NUNCA por tasa 0: una tasa 0 explícita es válida y no debe caer al default.
 */
export const DEFAULT_TAX_RATE = 19;

export interface CurrencyConfig {
  /** Código ISO 4217 efectivo (si el de entrada es desconocido, cae a CLP). */
  code: string;
  locale: string;
  /**
   * Decimales para display e inputs. BRL parte SIN centavos (decisión fase 1:
   * los precios de autos en Brasil no usan centavos y toda la tubería de
   * captura/columnas es entera — ver PLAN.md).
   */
  decimals: number;
  /**
   * Tope de decimales, SOLO para las monedas nuevas (COP/ARS: ISO dice 2
   * centavos, los precios de autos no los usan). Las tres de siempre no lo
   * definen y se formatean exactamente como hoy (byte-idéntico, ver harness).
   */
  maxDecimals?: number;
}

const CONFIGS: Record<string, Omit<CurrencyConfig, 'code'>> = {
  CLP: { locale: 'es-CL', decimals: 0 },
  USD: { locale: 'en-US', decimals: 2 },
  BRL: { locale: 'pt-BR', decimals: 0 },
  // Expansión LATAM (oct-2026). es-CO y es-AR formatean '$ 45.000.000' (con
  // espacio duro). Sin centavos, como CLP y BRL.
  COP: { locale: 'es-CO', decimals: 0, maxDecimals: 0 },
  ARS: { locale: 'es-AR', decimals: 0, maxDecimals: 0 },
};

/** Monedas REGISTRADAS, en el orden del récord (lo usa el select del alta de automotoras). */
export const CURRENCY_CODES: string[] = Object.keys(CONFIGS);

/**
 * ¿Precios en dólares SIN centavos? Solo un tenant de EE.UU. (por PAÍS, nunca
 * por moneda: hay una automotora chilena que vende en USD y sigue viendo
 * "$24,990.00" como siempre). Los autos en EE.UU. se publican "$24,990".
 */
const usdSinCentavos = (code: string, country?: string | null): boolean =>
  code === 'USD' && String(country || '').trim().toUpperCase() === 'US';

/**
 * Config por moneda. Desconocida/null → CLP (protege a los tenants chilenos de typos en BD).
 * `country` (opcional, clients.country): sin él, o con cualquier país que no sea
 * US, es exactamente la config de siempre.
 */
export const currencyConfig = (currency?: string | null, country?: string | null): CurrencyConfig => {
  const code = String(currency || DEFAULT_CURRENCY).toUpperCase();
  const cfg = CONFIGS[code];
  if (!cfg) return { code: DEFAULT_CURRENCY, ...CONFIGS[DEFAULT_CURRENCY] };
  if (usdSinCentavos(code, country)) return { code, ...cfg, decimals: 0, maxDecimals: 0 };
  return { code, ...cfg };
};

/**
 * Formatea un monto en la moneda del tenant.
 * Réplica EXACTA de los parámetros Intl de los helpers legacy (solo
 * minimumFractionDigits, nada más) para que el output CLP sea byte-idéntico:
 * '$1.234.567', negativo '$-1.234.567'. USD: '$1,234.50'. BRL: 'R$ 45.900' (NBSP).
 */
export const formatCurrency = (
  amount: number,
  currency: string | null | undefined = DEFAULT_CURRENCY,
  /** `country`: clients.country. Solo cambia algo para US (dólares sin centavos). */
  opts?: { includeSymbol?: boolean; country?: string | null }
): string => {
  const cfg = currencyConfig(currency, opts?.country);
  const includeSymbol = opts?.includeSymbol !== false;
  return new Intl.NumberFormat(cfg.locale, {
    style: includeSymbol ? 'currency' : 'decimal',
    currency: cfg.code,
    minimumFractionDigits: cfg.decimals,
    ...(cfg.maxDecimals !== undefined ? { maximumFractionDigits: cfg.maxDecimals } : {}),
  }).format(amount);
};

/** Símbolo de la moneda según su locale ('$' CLP/USD, 'R$' BRL). */
export const getCurrencySymbol = (currency?: string | null): string => {
  const cfg = currencyConfig(currency);
  const part = new Intl.NumberFormat(cfg.locale, { style: 'currency', currency: cfg.code })
    .formatToParts(1)
    .find((p) => p.type === 'currency');
  return part?.value ?? '$';
};

/**
 * Formato compacto para ejes/ticks de charts: '$12M', '$1.5M', 'R$ 60k'.
 * Abreviaturas fijas k/M/B con punto decimal JS, calcadas de los tickFormatters
 * manuales actuales de los dashboards (la migración por chart es fase 2 y debe
 * verificar paridad visual chart a chart).
 */
export const formatCompactCurrency = (
  amount: number,
  currency: string | null | undefined = DEFAULT_CURRENCY
): string => {
  const symbol = getCurrencySymbol(currency);
  const sign = amount < 0 ? '-' : '';
  const abs = Math.abs(amount);
  const scaled = (v: number, suffix: string) => {
    const num = Math.round(v * 10) / 10;
    const text = Number.isInteger(num) ? String(num) : num.toFixed(1);
    return `${sign}${symbol}${text}${suffix}`;
  };
  if (abs >= 1_000_000_000) return scaled(abs / 1_000_000_000, 'B');
  if (abs >= 1_000_000) return scaled(abs / 1_000_000, 'M');
  if (abs >= 1_000) return scaled(abs / 1_000, 'k');
  return `${sign}${symbol}${abs}`;
};

const separatorsFor = (cfg: CurrencyConfig): { group: string; decimal: string } => {
  const parts = new Intl.NumberFormat(cfg.locale, {
    style: 'decimal',
    minimumFractionDigits: 1,
  }).formatToParts(12345.6);
  return {
    group: parts.find((p) => p.type === 'group')?.value ?? '.',
    decimal: parts.find((p) => p.type === 'decimal')?.value ?? ',',
  };
};

/**
 * Parsea un string de monto (tipeado o pegado por el usuario, con o sin símbolo)
 * al número que representa, según los separadores del locale de la moneda.
 *
 * Blindaje anti-error-100x (footgun #2 de la auditoría): en monedas de 0 decimales
 * un sufijo decimal pegado ('50.000,00') se RECORTA en vez de concatenarse —
 * los strips legacy (`replace(/\D/g,'')`) convierten '50.000,00' en 5.000.000.
 * El separador de miles ('.' en es-CL/pt-BR) SIEMPRE se interpreta como miles.
 *
 * '' / null / basura → 0 (mismo contrato que extractNumber de PriceInput).
 */
export const unformatCurrency = (
  value: string | null | undefined,
  currency: string | null | undefined = DEFAULT_CURRENCY
): number => {
  if (value == null) return 0;
  const cfg = currencyConfig(currency);
  const { group, decimal } = separatorsFor(cfg);

  // Fuera símbolo/código de moneda, letras y espacios (incluye NBSP de pt-BR).
  let s = String(value).replace(/[^\d.,\-]/g, '');
  const negative = s.startsWith('-');
  s = s.replace(/-/g, '');
  if (!s) return 0;

  const lastDecimal = s.lastIndexOf(decimal);
  let intPart = s;
  let fracPart = '';
  // Un separador decimal solo cuenta como tal si deja 1-2 dígitos al final y no
  // reaparece después (',00' sí; '1,234,567' con decimal ',' del locale → miles mal
  // tipeados, se tratan como dígitos).
  if (lastDecimal !== -1) {
    const tail = s.slice(lastDecimal + 1);
    if (/^\d{1,2}$/.test(tail)) {
      intPart = s.slice(0, lastDecimal);
      fracPart = tail;
    }
  }
  const digits = (intPart.split(group).join('') || '0').replace(/\D/g, '');
  if (!digits && !fracPart) return 0;

  const base = Number(digits || '0');
  const withFrac =
    cfg.decimals > 0 && fracPart ? base + Number(fracPart) / 10 ** fracPart.length : base;
  const result = negative ? -withFrac : withFrac;
  return Number.isFinite(result) ? result : 0;
};

/**
 * Normaliza una tasa de impuesto leída de BD/props antes de pasarla a los módulos
 * fiscales (footgun #1 de la auditoría: `Number(null) === 0` → un null crudo
 * cerearía el IVA de todos los tenants en silencio).
 *
 *   null / undefined / '' / NaN / fuera de [0,100] → fallback (19)
 *   0 → 0 (tasa 0 explícita es VÁLIDA; la exención va por régimen, no por acá)
 *
 * Equivale a `??` con validación — JAMÁS usar `||` para esto.
 */
export const safeTaxRate = (value: unknown, fallback: number = DEFAULT_TAX_RATE): number => {
  if (value === null || value === undefined || value === '') return fallback;
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0 || num > 100) return fallback;
  return num;
};
